import { apiRequest } from "@/lib/api";

import type {
  CompanyLead,
  CompanyLeadListResponse,
  LeadAssignee,
  LeadPipelineStage,
  LeadStatus,
  PublicLeadPayload,
  PublicLeadResponse,
} from "@/types/lead";

export function createPublicLead(
  slug: string,
  payload: PublicLeadPayload | FormData,
) {
  return apiRequest<PublicLeadResponse>(
    `/leads/public/${encodeURIComponent(slug)}/`,
    {
      method: "POST",
      body:
        payload instanceof FormData
          ? payload
          : JSON.stringify(payload),
    },
  );
}

export function getCompanyLeads() {
  return apiRequest<CompanyLeadListResponse>(
    "/leads/",
    {
      method: "GET",
      authenticated: true,
    },
  );
}

export function updateCompanyLeadProduct(
  leadId: number,
  productId: number,
) {
  return apiRequest<CompanyLead>(
    `/leads/${leadId}/`,
    {
      method: "PATCH",
      authenticated: true,
      body: JSON.stringify({
        product: productId,
      }),
    },
  );
}


export function updateCompanyLeadStatus(
  leadId: number,
  status: LeadStatus,
) {
  return apiRequest<CompanyLead>(
    `/leads/${leadId}/`,
    {
      method: "PATCH",
      authenticated: true,
      body: JSON.stringify({
        status,
      }),
    },
  );
}


export type PipelineAppointment = {
  event_type: "visit" | "test_drive";
  starts_at: string;
  description: string;
};

export function updateCompanyLeadPipeline(
  leadId: number,
  pipelineStage: LeadPipelineStage,
  appointment?: PipelineAppointment,
) {
  return apiRequest<CompanyLead>(
    `/leads/${leadId}/pipeline/`,
    {
      method: "PATCH",
      authenticated: true,
      body: JSON.stringify({
        pipeline_stage: pipelineStage,
        ...(appointment ? { appointment } : {}),
      }),
    },
  );
}


export function getCompanyLead(
  leadId: number,
) {
  return apiRequest<CompanyLead>(
    `/leads/${leadId}/`,
    {
      method: "GET",
      authenticated: true,
    },
  );
}


export type LeadClientConversionResponse = {
  client_id: number;
  created: boolean;
  lead: CompanyLead;
};

export function convertCompanyLeadToClient(
  leadId: number,
  clientId?: number,
) {
  return apiRequest<LeadClientConversionResponse>(
    `/leads/${leadId}/convert/`,
    {
      method: "POST",
      authenticated: true,
      body: clientId === undefined ? undefined : JSON.stringify({ client_id: clientId }),
    },
  );
}

export function createCompanyVehicleLead(payload: {
  name: string; phone: string; email: string; message: string; cpf: string;
  origin: import("@/types/lead").LeadOrigin;
  product: number | null; assigned_to: number | null;
}) {
  return apiRequest<CompanyLead>("/leads/", {
    method: "POST", authenticated: true, body: JSON.stringify(payload),
  });
}


export function createLeadNote(
  leadId: number,
  description: string,
) {
  return apiRequest<CompanyLead>(
    `/leads/${leadId}/activities/`,
    {
      method: "POST",
      authenticated: true,
      body: JSON.stringify({
        description,
      }),
    },
  );
}

export function getLeadAssignees() {
  return apiRequest<LeadAssignee[]>(
    "/leads/assignees/",
    {
      method: "GET",
      authenticated: true,
    },
  );
}

export function updateCompanyLeadAssignment(
  leadId: number,
  assignedToId: number | null,
) {
  return apiRequest<CompanyLead>(
    "/leads/" + leadId + "/assign/",
    {
      method: "PATCH",
      authenticated: true,
      body: JSON.stringify({
        assigned_to_id: assignedToId,
      }),
    },
  );
}
